import type { BenefitDetailDto } from "./benefit-types"
import type { IntakeSelectionDraft } from "./intake-catalog"
import { formatPersonName } from "./person-identity"

/** Only labels/IDs needed by an open contact picker, not another saved household copy. */
export function intakeSecondaryOptions(benefit: BenefitDetailDto) {
  return benefit.personCollection.filter(person => person.id !== benefit.applicantId)
    .map(person => ({ id: person.id, label: formatPersonName(person) }))
}

export function intakeSecondaryAvailable(benefit: BenefitDetailDto, personId: string) {
  return !personId || (personId !== benefit.applicantId && benefit.personCollection.filter(person => person.id === personId).length === 1)
}

export function intakeDetailsDraft(benefit: BenefitDetailDto) {
  const selection = benefit.intakeSelection
  return {
    secondaryApplicantId: benefit.secondaryId ?? "", homePhone: benefit.homePhone ?? "",
    messagePhone: benefit.messagePhone ?? "", contactEmail: benefit.contactEmail ?? "",
    intakeType: benefit.intakeType ?? "", intakeLocation: benefit.intakeLocation ?? "",
    intakeSelection: selection?.kind === "unset"
      ? { kind: "unset" as const }
      : selection?.kind === "legacy"
        ? { kind: "legacy" as const, intakeType: selection.intakeType ?? benefit.intakeType ?? "", intakeLocation: selection.intakeLocation ?? benefit.intakeLocation ?? "" }
        : selection?.subtypeId && selection.code && selection.label && selection.version != null
          ? { kind: selection.kind, subtypeId: selection.subtypeId, code: selection.code, label: selection.label, version: selection.version }
          : null,
  }
}

export type IntakeDetailsDraft = ReturnType<typeof intakeDetailsDraft>

export function intakeDetailsRequest(draft: IntakeDetailsDraft, token: string | null, selection: IntakeSelectionDraft | null = draft.intakeSelection) {
  if (!token) throw new Error("Reload and reopen the contact editor before saving.")
  const phone = (value: string) => {
    if (!value.trim()) return ""
    if (!/^[0-9+().\- ]+$/.test(value)) throw new Error("Enter a ten-digit phone number, optionally with +1.")
    let digits = value.replace(/[^0-9]/g, "")
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1)
    if (digits.length !== 10) throw new Error("Enter a ten-digit phone number, optionally with +1.")
    return digits
  }
  return {
    concurrencyToken: token, secondaryApplicantId: draft.secondaryApplicantId || null,
    homePhone: phone(draft.homePhone), messagePhone: phone(draft.messagePhone), contactEmail: draft.contactEmail.trim(),
    // Keep untouched historical strings byte-for-byte; new catalog selections are canonical.
    intakeType: draft.intakeType, intakeLocation: draft.intakeLocation,
    ...(selection ? { intakeSelection: selection } : {}),
  }
}
